// BuyerProfile.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import CommonService from "../../services/common.service"; // adjust path if needed
import { Tabs, Modal } from 'antd';
import { toast } from 'react-toastify';
import '../../assets/styles/seller_profile.scss';
import { useBusinessTypeMaster } from "../../hooks/useBusinessTypeMaster";
import { useLocationMaster } from "../../hooks/useLocationMaster";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";

const { TabPane } = Tabs;

const initialState = {
  // basic company fields used by the form
  companyName: '',
  designation: '',
  trader: '',
  gstNumber: '',
  registrationNumber: '',
  website: '',
  noOfEmployees: '',
  yearEstablished: '',
  companyDescription: '',
  altContactName: '',
  altEmail: '',
  altContact: '',
  address: '',
  landmark: '',
  country: '',
  state: '',
  city: '',
  pincode: '',
  totalRevenue: '',
  exportPercentage: '',
  overseasAddress: '',
  iecNumber: '',
  timezone: '',
  currency: 'USD',
  // Supply Chain Logistics fields
  nearestPort: '',
  factoryToPortDistance: '',
  factoryToPortDistanceUnit: 'km',
  nearestAirport: '',
  factoryToAirportDistance: '',
  factoryToAirportDistanceUnit: 'km',
  nearestHighway: '',
  factoryToHighwayDistance: '',
  factoryToHighwayDistanceUnit: 'km'
};

export default function BuyerProfile() {
  const navigate = useNavigate();
  
  // Check user role and redirect if seller
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('trocanet_user') || '{}');
    const userTypeId = String(user.user_type_id || '').trim();
    const typeIds = userTypeId ? userTypeId.split(',').map(id => String(id).trim()) : [];
    
    // If user is only a seller (type 2) and not a buyer (type 1), redirect to seller profile
    if ((typeIds.includes('2') || userTypeId === '2') && !typeIds.includes('1') && userTypeId !== '1') {
      navigate('/seller-profile');
      return;
    }
  }, [navigate]);
  
  const [form, setForm] = useState(initialState);
  const [productInput, setProductInput] = useState('');
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  const [companyId, setCompanyId] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  // Master Data Hooks
  const { businessTypeOptions, timezoneOptions } = useBusinessTypeMaster();
  const {
    countries: rawCountries,
    states: rawStates,
    cities: rawCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();
  const { currencyOptions } = useCurrencyMaster();
  
  // Map master data to react-select format
  const countryOptions = rawCountries.map(c => ({ value: c.id, label: c.label }));
  const stateOptions = rawStates.map(s => ({ value: s.id, label: s.label }));
  const cityOptions = rawCities.map(c => ({ value: c.id, label: c.label }));
  
  const findOption = (options, val) => {
    if (!val || !options || options.length === 0) return null;
    return options.find((o) => 
      String(o.value) === String(val) || 
      Number(o.value) === Number(val) ||
      o.value === val
    ) || null;
  };
  
  const [descModal, setDescModal] = useState({ visible: false });
  const [descText, setDescText] = useState('');
  const [descFiles, setDescFiles] = useState([]);
  const uploadRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Additional optional pieces that the API expects:
  // languages, buyerProfile and businessTerms can be built from separate UI or left empty
  const [languages, setLanguages] = useState(['English']); // example default
  const [buyerProfile, setBuyerProfile] = useState(null); // set with real data if available
  const [businessTerms, setBusinessTerms] = useState(null); // set with real data if available
  
  // Certificates array - supports multiple certificates
  const [certificates, setCertificates] = useState([
    {
      name: '',
      issuer: '',
      issued_date: '',
      validity: '',
      attachment: null, // File object if uploaded
      attachment_url: '' // URL after upload
    }
  ]);

  // Media file URLs (stored after upload)
  const [mediaFiles, setMediaFiles] = useState({
    company_logo: '',
    profile_banner: '',
    brochure: '',
    company_video: ''
  });

  // Store profile data temporarily until master data is loaded
  const [profileData, setProfileData] = useState(null);
  const hasProcessedProfile = useRef(false);

  // Load existing profile on mount
  useEffect(() => {
    loadExistingProfile();
  }, []);

  // Memoize master data lengths to prevent unnecessary re-renders
  const countriesLoaded = rawCountries.length > 0;
  const timezonesLoaded = timezoneOptions.length > 0;
  const currenciesLoaded = currencyOptions.length > 0;
  const businessTypesLoaded = businessTypeOptions.length > 0;

  // Helper function to find ID from name/label
  const findIdByName = (options, name) => {
    if (!name || !options || options.length === 0) return '';
    // Try to find by label first (for countries, states, cities)
    const found = options.find(opt => 
      opt.label?.toLowerCase() === name.toLowerCase() || 
      String(opt.value) === String(name) ||
      String(opt.id) === String(name)
    );
    return found ? (found.value || found.id || '') : '';
  };

  // Store the profile's state and city names for later lookup
  const [pendingStateName, setPendingStateName] = useState(null);
  const [pendingCityName, setPendingCityName] = useState(null);
  const [pendingCountryId, setPendingCountryId] = useState(null);

  // Reload form values when master data becomes available (only once)
  useEffect(() => {
    if (profileData && countriesLoaded && timezonesLoaded && currenciesLoaded && businessTypesLoaded && !hasProcessedProfile.current) {
      const profile = profileData;

      // Find country ID first
      const countryId = findIdByName(rawCountries, profile.address?.country) || '';
      
      // Find timezone, currency, and business type (these don't depend on other data)
      const timezoneId = timezoneOptions.find(tz => 
        tz.label?.toLowerCase() === profile.address?.timezone?.toLowerCase() ||
        String(tz.value) === String(profile.address?.timezone)
      )?.value || '';
      const currencyId = currencyOptions.find(c => 
        c.currencyCode === profile.currency ||
        c.label === profile.currency ||
        String(c.value) === String(profile.currency)
      )?.value || profile.currency || 'USD';
      const businessTypeId = profile.company_type_id ? String(profile.company_type_id) : '';

      // Update form with country, timezone, currency, and business type first
      setForm(prev => ({
        ...prev,
        country: countryId,
        timezone: timezoneId,
        currency: currencyId,
        trader: businessTypeId || prev.trader,
      }));

      // Store state and city names for later lookup after states/cities are loaded
      if (countryId && profile.address?.state) {
        setPendingStateName(profile.address.state);
        setPendingCountryId(countryId);
      }
      if (profile.address?.city) {
        setPendingCityName(profile.address.city);
      }

      // Load states for the country
      if (countryId) {
        updateListsForCountry(countryId);
      }

      // Mark as processed to prevent re-running
      hasProcessedProfile.current = true;
    }
  }, [profileData, countriesLoaded, timezonesLoaded, currenciesLoaded, businessTypesLoaded]);

  // When states are loaded, find and set the state ID
  useEffect(() => {
    if (pendingStateName && rawStates.length > 0 && pendingCountryId) {
      const stateId = findIdByName(rawStates, pendingStateName);
      if (stateId) {
        setForm(prev => ({
          ...prev,
          state: stateId,
        }));
        
        // Load cities for this state
        updateListsForState(pendingCountryId, stateId);
        setPendingStateName(null); // Clear pending state name
      }
    }
  }, [rawStates, pendingStateName, pendingCountryId]);

  // When cities are loaded, find and set the city ID
  useEffect(() => {
    if (pendingCityName && rawCities.length > 0) {
      const cityId = findIdByName(rawCities, pendingCityName);
      if (cityId) {
        setForm(prev => ({
          ...prev,
          city: cityId,
        }));
        setPendingCityName(null); // Clear pending city name
      }
    }
  }, [rawCities, pendingCityName]);

  const loadExistingProfile = async () => {
    try {
      setIsLoadingProfile(true);
      const response = await CommonService.get('/company/my-profile');
      
      if (response && response.success && response.data) {
        const profile = response.data;
        
        // Check if profile role is SELLER only (not BUYER or BOTH)
        if (profile.company_role === 'SELLER') {
          toast.error('This page is for buyers only. Redirecting to seller profile...');
          navigate('/seller-profile');
          return;
        }
        
        setCompanyId(profile.company_id || profile.id);
        
        // Reset the processed flag when loading a new profile
        hasProcessedProfile.current = false;
        
        // Store profile data for later processing when master data is available
        setProfileData(profile);
        
        // Helper function to find ID from name/label
        const findIdByName = (options, name) => {
          if (!name || !options || options.length === 0) return '';
          // Try to find by label first (for countries, states, cities)
          const found = options.find(opt => 
            opt.label?.toLowerCase() === name.toLowerCase() || 
            String(opt.value) === String(name) ||
            String(opt.id) === String(name)
          );
          return found ? (found.value || found.id || '') : '';
        };

        // Find IDs from names for Select components (if master data is available)
        const countryId = findIdByName(rawCountries, profile.address?.country) || '';
        const stateId = findIdByName(rawStates, profile.address?.state) || '';
        const cityId = findIdByName(rawCities, profile.address?.city) || '';
        const timezoneId = timezoneOptions.length > 0 ? (timezoneOptions.find(tz => 
          tz.label?.toLowerCase() === profile.address?.timezone?.toLowerCase() ||
          String(tz.value) === String(profile.address?.timezone)
        )?.value || '') : '';
        const currencyId = currencyOptions.length > 0 ? (currencyOptions.find(c => 
          c.currencyCode === profile.currency ||
          c.label === profile.currency ||
          String(c.value) === String(profile.currency)
        )?.value || profile.currency || 'USD') : (profile.currency || 'USD');

        // Populate form with existing data
        setForm(prev => ({
          ...prev,
          companyName: profile.company_name || '',
          designation: profile.designation || '',
          trader: profile.company_type_id ? String(profile.company_type_id) : '',
          gstNumber: profile.tax_number || '',
          registrationNumber: profile.registration_number || '',
          website: profile.website || '',
          noOfEmployees: profile.number_of_employees || '',
          yearEstablished: profile.establishment_year || '',
          companyDescription: profile.company_description || '',
          altContactName: profile.contact_details?.alternate_contact_name || '',
          altEmail: profile.contact_details?.alternate_email || '',
          altContact: profile.contact_details?.alternate_contact_number || '',
          address: profile.address?.address || '',
          landmark: profile.address?.landmark || '',
          country: countryId,
          state: stateId,
          city: cityId,
          pincode: profile.address?.pincode || '',
          totalRevenue: profile.annual_turnover || '',
          exportPercentage: profile.export_percentage || profile.seller_profile?.export_percentage || '',
          overseasAddress: profile.overseas_address?.address || '',
          iecNumber: profile.iec_number || '',
          timezone: timezoneId,
          currency: currencyId,
        }));

        // Load media files from profile
        if (profile.media) {
          setMediaFiles({
            company_logo: profile.media.company_logo || '',
            profile_banner: profile.media.profile_banner || '',
            brochure: profile.media.brochure || '',
            company_video: profile.media.company_video || ''
          });
        }

        // Load products
        if (profile.main_products && Array.isArray(profile.main_products)) {
          setProducts(profile.main_products);
        }

        // Load languages
        if (profile.languages && Array.isArray(profile.languages)) {
          setLanguages(profile.languages);
        }

        // Load buyer profile
        if (profile.buyer_profile) {
          setBuyerProfile(profile.buyer_profile);

          // Load Supply Chain Logistics from buyer_profile
          if (profile.buyer_profile.supply_chain_logistics) {
            const logistics = profile.buyer_profile.supply_chain_logistics;
            setForm(prevForm => ({
              ...prevForm,
              nearestPort: logistics.nearest_port || '',
              factoryToPortDistance: logistics.factory_to_port_distance?.value?.toString() || '',
              factoryToPortDistanceUnit: logistics.factory_to_port_distance?.unit || 'km',
              nearestAirport: logistics.nearest_airport || '',
              factoryToAirportDistance: logistics.factory_to_airport_distance?.value?.toString() || '',
              factoryToAirportDistanceUnit: logistics.factory_to_airport_distance?.unit || 'km',
              nearestHighway: logistics.nearest_highway || '',
              factoryToHighwayDistance: logistics.factory_to_highway_distance?.value?.toString() || '',
              factoryToHighwayDistanceUnit: logistics.factory_to_highway_distance?.unit || 'km'
            }));
          }
        }

        // Load business terms
        if (profile.business_terms) {
          setBusinessTerms(profile.business_terms);
        }

        // Load certificates
        if (profile.certifications && Array.isArray(profile.certifications) && profile.certifications.length > 0) {
          setCertificates(profile.certifications.map(cert => ({
            name: cert.name || '',
            issuer: cert.issuer || '',
            issued_date: cert.issued_date || cert.certificateStartDate || '',
            validity: cert.validity || cert.certificateEndDate || '',
            attachment: null, // Files are not stored in profile, so set to null
            attachment_url: cert.attachment_url || cert.attachment || '' // Load existing attachment URL if available
          })));
        } else {
          // Initialize with one empty certificate if none exist
          setCertificates([{
            name: '',
            issuer: '',
            issued_date: '',
            validity: '',
            attachment: null,
            attachment_url: ''
          }]);
        }

        // Load states and cities if country is set (use ID, not name)
        if (countryId) {
          updateListsForCountry(countryId);
          if (stateId) {
            updateListsForState(countryId, stateId);
          }
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Profile doesn't exist yet, that's OK - user will create new one
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const distanceUnitOptions = [
    { id: 'km', label: 'KM' },
    { id: 'miles', label: 'Miles' },
    { id: 'meters', label: 'Meters' }
  ];

  // Handle certificate field changes
  const handleCertificateChange = (index, field, value) => {
    setCertificates(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  // Add a new certificate entry
  const handleAddCertificate = () => {
    setCertificates(prev => [...prev, {
      name: '',
      issuer: '',
      issued_date: '',
      validity: '',
      attachment: null,
      attachment_url: ''
    }]);
  };

  // Remove a certificate entry
  const handleRemoveCertificate = (index) => {
    if (certificates.length > 1) {
      setCertificates(prev => prev.filter((_, i) => i !== index));
    } else {
      // If only one certificate, reset it instead of removing
      setCertificates([{
        name: '',
        issuer: '',
        issued_date: '',
        validity: '',
        attachment: null,
        attachment_url: ''
      }]);
    }
  };

  // Handle certificate file upload
  const handleCertificateFileChange = async (index, e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    // Store file object temporarily
    handleCertificateChange(index, 'attachment', file);

    try {
      setLoading(true);
      const fileUrl = await uploadFile(file, `certificate_${index}`, 'company');
      handleCertificateChange(index, 'attachment_url', fileUrl);
      toast.success('Certificate attachment uploaded successfully');
    } catch (error) {
      console.error('Error uploading certificate attachment:', error);
      // Remove file if upload failed
      handleCertificateChange(index, 'attachment', null);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked, multiple } = e.target;
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: checked }));
      return;
    }
    if (type === 'select-multiple' || multiple) {
      const selectedValues = Array.from(e.target.selectedOptions, option => option.value);
      setForm(prev => ({ ...prev, [name]: selectedValues }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle location changes (country, state, city) with cascading updates
  const handleLocationChange = (name, selected) => {
    const value = selected ? selected.value : '';
    
    if (name === 'country') {
      // Clear dependent values and update country
      setForm(prev => ({ ...prev, country: value, state: '', city: '' }));
      // Load states for this country
      if (value) updateListsForCountry(value);
    } else if (name === 'state') {
      // Clear city and update state
      setForm(prev => {
        const updated = { ...prev, state: value, city: '' };
        // Load cities for selected state
        if (value && updated.country) {
          updateListsForState(updated.country, value);
        }
        return updated;
      });
    } else {
      // Just update the field (city)
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const addProduct = () => {
    const v = productInput.trim();
    if (!v) return;
    if (!products.includes(v)) setProducts(prev => [...prev, v]);
    setProductInput('');
  };

  const removeProduct = (p) => setProducts(prev => prev.filter(x => x !== p));

  // Upload file to backend and return URL
  const uploadFile = async (file, fileType, category = 'company') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('companyName', form.companyName || 'default');
      formData.append('fileType', fileType);

      // Don't set Content-Type header - axios will set it automatically with boundary
      const response = await CommonService.post('/upload/single', formData);

      if (response && response.success && response.data) {
        // Return the access path (URL) from the response
        return response.data.accessPath || response.data.filePath || '';
      }
      throw new Error(response?.message || 'Upload failed');
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.[0] || error?.message || `Failed to upload ${fileType}`;
      toast.error(errorMessage);
      throw error;
    }
  };

  // Handle media file uploads (company logo, banner, brochure, video)
  const handleMediaFileChange = async (fileType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const fileUrl = await uploadFile(file, fileType);
      setMediaFiles(prev => ({
        ...prev,
        [fileType]: fileUrl
      }));
      toast.success(`${fileType.replace('_', ' ')} uploaded successfully`);
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (e) => {
    // Legacy handler - can be removed if not used elsewhere
    const files = Array.from(e.target.files || []);
    console.log('selected files', files);
  };

  const handleDescFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    setDescFiles(files);
  };

  /**
   * buildApiPayload
   * Produces the JSON payload that matches the cURL you provided earlier.
   * Uses data from `form`, `products`, `languages`, optional `buyerProfile` and `businessTerms`.
   */
  const buildApiPayload = () => {
    // Map certificates array to API format
    // Filter out empty certificates (where all fields are empty)
    const certifications = certificates
      .filter(cert => cert.name || cert.issuer || cert.validity || cert.issued_date)
      .map(cert => ({
        name: cert.name || '',
        issuer: cert.issuer || '',
        issued_date: cert.issued_date && cert.issued_date.trim() !== '' ? cert.issued_date.trim() : undefined,
        validity: cert.validity || '',
        certificate_no: '', // Backend expects this field but we're not using it anymore
        attachment_url: cert.attachment_url || undefined // Include attachment URL if uploaded
      }));

    // Map business type to company_type_id
    // form.trader should be the business_type_id value from the dropdown
    const company_type_id = form.trader ? Number(form.trader) : null;
    const company_role = 'BUYER'; // Buyer profile page always uses BUYER role

    // Build buyer_profile and business_terms if app has values; fallback to `buyerProfile` / `businessTerms` state
    // Remove null/empty values to avoid validation errors
    const buyer_profile = buyerProfile || {
      preferred_suppliers: [],
      sourcing_regions: [],
      procurement_categories: [],
      preferred_certifications: [],
      supplier_requirements: []
      // purchase_volume: only include if it's an object, otherwise omit
      // sourcing_timeline: only include if it's a non-empty string, otherwise omit
    };
    
    // Clean buyer_profile - remove null/empty values
    if (buyer_profile.purchase_volume === null || buyer_profile.purchase_volume === undefined) {
      delete buyer_profile.purchase_volume;
    }
    if (!buyer_profile.sourcing_timeline || buyer_profile.sourcing_timeline.trim() === '') {
      delete buyer_profile.sourcing_timeline;
    }

        // Build Supply Chain Logistics data
    const supply_chain_logistics = {};
    if (form.nearestPort && form.nearestPort.trim() !== '') {
      supply_chain_logistics.nearest_port = form.nearestPort.trim();
    }
    if (form.factoryToPortDistance && form.factoryToPortDistance.toString().trim() !== '') {
      const portDistanceValue = Number(form.factoryToPortDistance.toString().trim());
      if (!isNaN(portDistanceValue) && portDistanceValue >= 0) {
        supply_chain_logistics.factory_to_port_distance = {
          value: portDistanceValue,
          unit: form.factoryToPortDistanceUnit || 'km'
        };
      }
    }
    if (form.nearestAirport && form.nearestAirport.trim() !== '') {
      supply_chain_logistics.nearest_airport = form.nearestAirport.trim();
    }
    if (form.factoryToAirportDistance && form.factoryToAirportDistance.toString().trim() !== '') {
      const airportDistanceValue = Number(form.factoryToAirportDistance.toString().trim());
      if (!isNaN(airportDistanceValue) && airportDistanceValue >= 0) {
        supply_chain_logistics.factory_to_airport_distance = {
          value: airportDistanceValue,
          unit: form.factoryToAirportDistanceUnit || 'km'
        };
      }
    }
    if (form.nearestHighway && form.nearestHighway.trim() !== '') {
      supply_chain_logistics.nearest_highway = form.nearestHighway.trim();
    }
    if (form.factoryToHighwayDistance && form.factoryToHighwayDistance.toString().trim() !== '') {
      const highwayDistanceValue = Number(form.factoryToHighwayDistance.toString().trim());
      if (!isNaN(highwayDistanceValue) && highwayDistanceValue >= 0) {
        supply_chain_logistics.factory_to_highway_distance = {
          value: highwayDistanceValue,
          unit: form.factoryToHighwayDistanceUnit || 'km'
        };
      }
    }
    
    // Add logistics to buyer_profile if any logistics data exists
    if (Object.keys(supply_chain_logistics).length > 0) {
      buyer_profile.supply_chain_logistics = supply_chain_logistics;
    }

    const business_terms_payload = businessTerms || {
      payment_terms: [],
      shipping_terms: []
      // sample_terms: only include if non-empty
      // minimum_order_value: only include if it's an object
      // lead_time: only include if it's an object
    };
    
    // Clean business_terms - remove null/empty values
    if (!business_terms_payload.sample_terms || business_terms_payload.sample_terms.trim() === '') {
      delete business_terms_payload.sample_terms;
    }
    if (business_terms_payload.minimum_order_value === null || business_terms_payload.minimum_order_value === undefined) {
      delete business_terms_payload.minimum_order_value;
    }
    if (business_terms_payload.lead_time === null || business_terms_payload.lead_time === undefined) {
      delete business_terms_payload.lead_time;
    }

    // Clear previous validation errors
    setValidationErrors({});

    // Validate required fields before building payload
    const errors = {};
    
    if (!form.companyName || form.companyName.trim() === '') {
      errors.companyName = 'Company Name is required';
    }
    if (!form.designation || form.designation.trim() === '') {
      errors.designation = 'Designation is required';
    }
    if (!company_type_id) {
      errors.trader = 'Business Type is required';
    }
    if (!form.registrationNumber || form.registrationNumber.trim() === '') {
      errors.registrationNumber = 'Registration Number is required';
    }
    if (!form.yearEstablished) {
      errors.yearEstablished = 'Year Established is required';
    } else {
      const year = Number(form.yearEstablished);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        errors.yearEstablished = `Year must be between 1900 and ${currentYear}`;
      }
    }
    if (!form.noOfEmployees || String(form.noOfEmployees).trim() === '') {
      errors.noOfEmployees = 'Number of Employees is required';
    }
    if (!products || products.length === 0) {
      errors.mainProducts = 'At least one Main Product is required';
    }
    if (!form.address || form.address.trim() === '') {
      errors.address = 'Address is required';
    }
    if (!form.city || form.city === '') {
      errors.city = 'City is required';
    }
    if (!form.state || form.state === '') {
      errors.state = 'State is required';
    }
    if (!form.country || form.country === '') {
      errors.country = 'Country is required';
    }
    if (!form.pincode || form.pincode.trim() === '') {
      errors.pincode = 'Pincode is required';
    }
    if (!form.landmark || form.landmark.trim() === '') {
      errors.landmark = 'Landmark is required';
    }
    if (!form.timezone || form.timezone === '') {
      errors.timezone = 'Timezone is required';
    }
    if (!form.altContactName || form.altContactName.trim() === '') {
      errors.altContactName = 'Alternate Contact Name is required';
    }
    if (!form.altEmail || form.altEmail.trim() === '') {
      errors.altEmail = 'Alternate Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(form.altEmail)) {
      errors.altEmail = 'Please enter a valid email address';
    }
    if (!form.altContact || form.altContact.trim() === '') {
      errors.altContact = 'Alternate Contact Number is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      throw new Error('Please fix the validation errors before saving');
    }

    // Convert IDs to string names for backend
    // Find country name from ID
    const countryObj = rawCountries.find(c => c.id === form.country || String(c.id) === String(form.country));
    const countryName = countryObj ? countryObj.label : String(form.country);
    
    // Find state name from ID
    const stateObj = rawStates.find(s => s.id === form.state || String(s.id) === String(form.state));
    const stateName = stateObj ? stateObj.label : String(form.state);
    
    // Find city name from ID
    const cityObj = rawCities.find(c => c.id === form.city || String(c.id) === String(form.city));
    const cityName = cityObj ? cityObj.label : String(form.city);
    
    // Find timezone name from ID
    const timezoneObj = timezoneOptions.find(tz => tz.value === form.timezone || String(tz.value) === String(form.timezone));
    const timezoneName = timezoneObj ? timezoneObj.label : String(form.timezone);
    
    // Find currency code from ID
    const currencyObj = currencyOptions.find(c => c.value === form.currency || String(c.value) === String(form.currency));
    const currencyCode = currencyObj ? (currencyObj.currencyCode || currencyObj.label || String(form.currency)) : String(form.currency || 'USD');

    // Final payload matching the backend validation schema
    const payload = {
      company_name: form.companyName,
      designation: form.designation,
      company_type_id: company_type_id,
      company_role: company_role,
      registration_number: form.registrationNumber,
      tax_number: form.gstNumber || '', // Optional but send empty string if not provided
      establishment_year: Number(form.yearEstablished),
      annual_turnover: form.totalRevenue || '', // Optional
      number_of_employees: String(form.noOfEmployees || ""),
      website: form.website && form.website.trim() !== '' ? 
        (form.website.startsWith('http://') || form.website.startsWith('https://') ? form.website : `https://${form.website}`) 
        : undefined, // Only include if provided (must be valid URI)
      company_description: form.companyDescription || undefined,
      currency: currencyCode, // Convert to string (currency code)
      iec_number: form.iecNumber && form.iecNumber.trim() !== '' ? form.iecNumber.trim() : undefined,
      export_percentage: form.exportPercentage && form.exportPercentage.toString().trim() !== '' ? 
        (() => {
          const value = Number(form.exportPercentage.toString().trim());
          return !isNaN(value) && value >= 0 && value <= 100 ? value : undefined;
        })() : undefined,
      main_products: products,
      media: {
        company_logo: mediaFiles.company_logo || "https://cdn.example.com/placeholder-logo.png", // Use uploaded URL or placeholder
        profile_banner: mediaFiles.profile_banner || undefined,
        brochure: mediaFiles.brochure || undefined,
        product_catalogue: undefined, // Not used in buyer profile
        company_video: mediaFiles.company_video || undefined
      },
      address: {
        address: String(form.address),
        city: cityName, // Convert ID to string name
        state: stateName, // Convert ID to string name
        country: countryName, // Convert ID to string name
        timezone: timezoneName, // Convert ID to string name
        landmark: String(form.landmark),
        pincode: String(form.pincode)
      },
      overseas_address: form.overseasAddress && form.overseasAddress.trim() !== '' ? {
        address: String(form.overseasAddress),
        city: '', // Optional - can be added later if needed
        state: '', // Optional - can be added later if needed
        country: '' // Optional - can be added later if needed
      } : undefined,
      contact_details: {
        alternate_contact_name: form.altContactName,
        alternate_email: form.altEmail,
        alternate_contact_number: form.altContact
      },
      buyer_profile: buyer_profile,
      business_terms: business_terms_payload,
      certifications: certifications.length > 0 ? certifications : undefined,
      languages: languages.length > 0 ? languages : undefined,
      social_media: {
        linkedin: form.linkedin || undefined,
        twitter: form.twitter || undefined
      }
    };

    // Remove undefined fields to avoid validation issues
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });
    
    // Clean nested objects
    if (payload.media) {
      Object.keys(payload.media).forEach(key => {
        if (payload.media[key] === undefined) {
          delete payload.media[key];
        }
      });
    }
    
    if (payload.social_media) {
      Object.keys(payload.social_media).forEach(key => {
        if (payload.social_media[key] === undefined) {
          delete payload.social_media[key];
        }
      });
      if (Object.keys(payload.social_media).length === 0) {
        delete payload.social_media;
      }
    }

    return payload;
  };

  /**
   * saveCompanyProfiles
   * Sends the payload to POST /api/v1/company/profile (create) or PUT /api/v1/company/profile/:companyId (update)
   */
  const saveCompanyProfiles = async () => {
    const payload = buildApiPayload();
    console.log('payload ready to send:', payload);

    try {
      let response;
      if (companyId) {
        // Update existing profile
        response = await CommonService.put(`/company/profile/${companyId}`, payload);
        if (response && response.success) {
          toast.success(response.message || 'Company profile updated successfully');
          return companyId;
        } else {
          toast.error("Company Profile update failed");
          return null;
        }
      } else {
        // Create new profile
        response = await CommonService.post(`/company/profile`, payload);
        if (response && response.success) {
          const newCompanyId = response.data?.company_id || response.data?.id;
          if (newCompanyId) {
            setCompanyId(newCompanyId);
          }
          toast.success(response.message || 'Company profile created successfully');
          return newCompanyId;
        } else {
          toast.error("Company Profile creation failed");
          return null;
        }
      }
    } catch (err) {
      console.error('API Error:', err);
      console.error('Error Response:', err?.response?.data);
      
      // Show detailed validation errors
      let message = err?.response?.data?.message || 
        (companyId ? "Company Profile update failed. Please try again." : "Company Profile creation failed. Please try again.");
      
      // If there are validation errors, show them
      if (err?.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        message = err.response.data.errors.join(', ');
      } else if (err?.response?.data?.error) {
        message = err.response.data.error;
      }
      
      toast.error(message);
      return null;
    }


    // Try CommonService if it's already configured in your app
    // try {
    //   if (typeof CommonService !== 'undefined' && CommonService?.post) {
    //     // if CommonService expects relative path
    //     const path = '/api/v1/company/profile';
    //     const resp = await CommonService.post(path, payload);
    //     // adapt to your CommonService/response shape
    //     if (resp && resp.success) {
    //       toast.success(resp.message || 'Company profile created successfully');
    //       return resp.data?.company_id ?? null;
    //     } else {
    //       toast.error(resp?.message || 'Company Profile creation failed');
    //       return null;
    //     }
    //   }
    // } catch (err) {
    //   console.warn('CommonService.post failed — will fallback to fetch', err);
    // }

    // // Fallback: use fetch directly.
    // try {
    //   // try to pick token from localStorage if available
    //   let token = null;
    //   try { token = window?.localStorage?.getItem('trocanet_access_token') || window?.localStorage?.getItem('access_token'); } catch (e) { /* ignore */ }

    //   const response = await fetch('/api/v1/company/profile', {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //       ...(token ? { Authorization: `Bearer ${token}` } : {})
    //     },
    //     body: JSON.stringify(payload)
    //   });

    //   const data = await response.json().catch(() => null);
    //   if (!response.ok) {
    //     const msg = data?.message || `HTTP ${response.status}`;
    //     toast.error(msg);
    //     console.error('API error:', data);
    //     return null;
    //   }

    //   toast.success(data?.message || 'Company profile created successfully');
    //   return data?.data?.company_id ?? data?.company_id ?? null;
    // } catch (err) {
    //   console.error('Network or server error:', err);
    //   toast.error(err?.message || 'Company Profile creation failed. Please try again.');
    //   return null;
    // }


  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const createdId = await saveCompanyProfiles();
      if (createdId) {
        console.log('Company created/updated with id:', createdId);
        toast.success('Profile saved successfully!');
        // Reload profile to get latest data
        await loadExistingProfile();
        // If this save is part of Logistics onboarding, continue to Logistics profile next
        const params = new URLSearchParams(window.location.search);
        const onboarding = params.get('onboarding');
        const nextRoute =
          onboarding === 'logistics' ? '/logistics-profile/edit?onboarding=logistics' : '/profile/view';

        setTimeout(() => {
          navigate(nextRoute);
        }, 800);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.message || 'Failed to save profile. Please check all required fields.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="seller-profile container-fluid">
        <div className="row">
          <main className="col-12 sp-main">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <nav aria-label="breadcrumb" className="sp-breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><a href="/" className="breadcrumb-link">Home</a></li>
                  <li className="breadcrumb-item active" aria-current="page">Profile</li>
                </ol>
              </nav>

              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-primary" onClick={() => navigate('/')}>Seller Profile</button>
                <button type="button" className="btn btn-primary" onClick={() => navigate('/buyer-rfq-form')}>RFQ</button>
                <button type="button" className="btn btn-primary" onClick={() => navigate('/submit-quotation-to-buyer')}>Submit Quotation</button>
              </div>
            </div>

            <Tabs className="sp-card" activeKey={activeTab} onChange={setActiveTab}>
              <TabPane
                tab={
                  <span>
                    <span className="tab-icon">📇</span> Company Details
                  </span>
                }
                key="1"
              >
                <div className="container-fluid">
                  <section className="sp-section">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">🏢 Company</h6>

                      <div className="row g-2">
                        <div className="col-12 col-md-6 col-lg-3">
                          <input
                            className={`input-pill form-control ${validationErrors.companyName ? 'is-invalid' : ''}`}
                            placeholder="Name of the Company *"
                            name="companyName"
                            value={form.companyName}
                            onChange={onChange}
                          />
                          {validationErrors.companyName && (
                            <div className="invalid-feedback d-block">{validationErrors.companyName}</div>
                          )}
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input
                            className="input-pill form-control"
                            placeholder="Designation *"
                            name="designation"
                            value={form.designation}
                            onChange={onChange}
                          />
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <Select
                            className={`input-pill p-0 ${validationErrors.trader ? 'is-invalid' : ''}`}
                            name="trader"
                            placeholder="Business Type *"
                            options={businessTypeOptions}
                            onChange={(opt) => {
                              onChange({ target: { name: "trader", value: opt?.value || "" } });
                              // Clear validation error when user selects
                              if (validationErrors.trader) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.trader;
                                  return newErrors;
                                });
                              }
                            }}
                            value={findOption(businessTypeOptions, form.trader)}
                            isClearable
                          />
                          {validationErrors.trader && (
                            <div className="invalid-feedback d-block">{validationErrors.trader}</div>
                          )}
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input className="input-pill form-control" placeholder="GST Number" name="gstNumber" value={form.gstNumber} onChange={onChange} />
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input 
                            className={`input-pill form-control ${validationErrors.registrationNumber ? 'is-invalid' : ''}`}
                            placeholder="Company Registration Number *" 
                            name="registrationNumber" 
                            value={form.registrationNumber} 
                            onChange={onChange} 
                          />
                          {validationErrors.registrationNumber && (
                            <div className="invalid-feedback d-block">{validationErrors.registrationNumber}</div>
                          )}
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input className="input-pill form-control" placeholder="Website (https://example.com)" name="website" value={form.website} onChange={onChange} />
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input 
                            type="number" 
                            className={`input-pill form-control ${validationErrors.noOfEmployees ? 'is-invalid' : ''}`}
                            placeholder="No of Employees *" 
                            name="noOfEmployees" 
                            min="0" 
                            value={form.noOfEmployees} 
                            onChange={onChange} 
                          />
                          {validationErrors.noOfEmployees && (
                            <div className="invalid-feedback d-block">{validationErrors.noOfEmployees}</div>
                          )}
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input 
                            className={`input-pill form-control ${validationErrors.yearEstablished ? 'is-invalid' : ''}`}
                            placeholder="Year Established (YYYY) *" 
                            name="yearEstablished" 
                            value={form.yearEstablished} 
                            onChange={onChange} 
                          />
                          {validationErrors.yearEstablished && (
                            <div className="invalid-feedback d-block">{validationErrors.yearEstablished}</div>
                          )}
                        </div>
                      </div>

                      <div className="row g-2 mt-3">
                        <div className="col-12">
                          <textarea className="input-area form-control" placeholder="Company Description" name="companyDescription" value={form.companyDescription} onChange={onChange} rows="4" />
                        </div>
                      </div>

                      <div className="row g-2 mt-3">
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="file-upload-component">
                            <div className="file-title">Company Logo <span className="required-asterisk">*</span></div>
                            <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                              <div className="file-content d-flex align-items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon">
                                  <path d="M12 3v12"></path><path d="m17 8-5-5-5 5"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                </svg>
                                <span className="choose-text">Choose file</span>
                              </div>
                              <input type="file" accept=".png,.jpg,.gif" style={{ display: 'none' }} onChange={(e) => handleMediaFileChange('company_logo', e)} />
                              {mediaFiles.company_logo && (
                                <div className="mt-2">
                                  <div className="d-flex align-items-center gap-2">
                                    {mediaFiles.company_logo.match(/\.(jpg|jpeg|png|gif)$/i) && (
                                      <img src={mediaFiles.company_logo} alt="Company Logo" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                                    )}
                                    <div className="flex-grow-1">
                                      <div className="small text-success">✓ File uploaded</div>
                                      <div className="small text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                        {mediaFiles.company_logo.split('/').pop()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </label>
                            <div className="file-types small text-muted mt-1">PNG, JPG, GIF</div>
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="file-upload-component">
                            <div className="file-title">Profile Banner</div>
                            <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                              <div className="file-content d-flex align-items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon">
                                  <path d="M12 3v12"></path><path d="m17 8-5-5-5 5"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                </svg>
                                <span className="choose-text">Choose file</span>
                              </div>
                              <input type="file" accept=".png,.jpg" style={{ display: 'none' }} onChange={(e) => handleMediaFileChange('profile_banner', e)} />
                              {mediaFiles.profile_banner && (
                                <div className="mt-2">
                                  <div className="d-flex align-items-center gap-2">
                                    {mediaFiles.profile_banner.match(/\.(jpg|jpeg|png)$/i) && (
                                      <img src={mediaFiles.profile_banner} alt="Profile Banner" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                                    )}
                                    <div className="flex-grow-1">
                                      <div className="small text-success">✓ File uploaded</div>
                                      <div className="small text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                        {mediaFiles.profile_banner.split('/').pop()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </label>
                            <div className="file-types small text-muted mt-1">PNG, JPG</div>
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="file-upload-component">
                            <div className="file-title">Brochure</div>
                            <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                              <div className="file-content d-flex align-items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon">
                                  <path d="M12 3v12"></path><path d="m17 8-5-5-5 5"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                </svg>
                                <span className="choose-text">Choose file</span>
                              </div>
                              <input type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={(e) => handleMediaFileChange('brochure', e)} />
                              {mediaFiles.brochure && (
                                <div className="mt-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6c757d' }}>
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                      <polyline points="14 2 14 8 20 8"></polyline>
                                      <line x1="16" y1="13" x2="8" y2="13"></line>
                                      <line x1="16" y1="17" x2="8" y2="17"></line>
                                      <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                    <div className="flex-grow-1">
                                      <div className="small text-success">✓ File uploaded</div>
                                      <div className="small text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                        {mediaFiles.brochure.split('/').pop()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </label>
                            <div className="file-types small text-muted mt-1">PDF, DOC, DOCX</div>
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="file-upload-component">
                            <div className="file-title">Company Video</div>
                            <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                              <div className="file-content d-flex align-items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon">
                                  <path d="M12 3v12"></path><path d="m17 8-5-5-5 5"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                </svg>
                                <span className="choose-text">Choose file</span>
                              </div>
                              <input type="file" accept=".mp4,.mov,.avi" style={{ display: 'none' }} onChange={(e) => handleMediaFileChange('company_video', e)} />
                              {mediaFiles.company_video && (
                                <div className="mt-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6c757d' }}>
                                      <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                    </svg>
                                    <div className="flex-grow-1">
                                      <div className="small text-success">✓ File uploaded</div>
                                      <div className="small text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                        {mediaFiles.company_video.split('/').pop()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </label>
                            <div className="file-types small text-muted mt-1">MP4, MOV, AVI</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="sp-subcard mt-3">
                      <h6 className="sp-section-title">🔧 Main Products <span className="required-asterisk">*</span></h6>
                      <div className="product-input-row d-flex gap-2">
                        <input className="input-pill form-control" placeholder="eg. chilli powder" value={productInput} onChange={(e) => setProductInput(e.target.value)} />
                        <button type="button" className="btn btn-outline-secondary" onClick={addProduct}>+ Add</button>
                      </div>
                      {validationErrors.mainProducts && (
                        <div className="text-danger small mt-2">{validationErrors.mainProducts}</div>
                      )}
                      <div className="tags mt-2">
                        {products.map(p => (
                          <span className="tag badge bg-light text-dark me-1" key={p}>
                            {p}
                            <button type="button" className="btn btn-sm btn-link p-0 ms-2" onClick={() => removeProduct(p)}>×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </section>

                  <section className="sp-section mt-3">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">📞 Contact Details</h6>
                      <div className="row g-2">
                        <div className="col-12 col-md-6 col-lg-4">
                          <input 
                            className={`input-pill form-control ${validationErrors.altContactName ? 'is-invalid' : ''}`}
                            placeholder="Alternate Contact Name *" 
                            name="altContactName" 
                            value={form.altContactName} 
                            onChange={onChange} 
                          />
                          {validationErrors.altContactName && (
                            <div className="invalid-feedback d-block">{validationErrors.altContactName}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                          <input 
                            className={`input-pill form-control ${validationErrors.altEmail ? 'is-invalid' : ''}`}
                            placeholder="Alternate Email (email@example.com) *" 
                            name="altEmail" 
                            value={form.altEmail} 
                            onChange={onChange} 
                            type="email" 
                          />
                          {validationErrors.altEmail && (
                            <div className="invalid-feedback d-block">{validationErrors.altEmail}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-4">
                          <input 
                            className={`input-pill form-control ${validationErrors.altContact ? 'is-invalid' : ''}`}
                            placeholder="Alternate Contact (+91 1234567890) *" 
                            name="altContact" 
                            value={form.altContact} 
                            onChange={onChange} 
                          />
                          {validationErrors.altContact && (
                            <div className="invalid-feedback d-block">{validationErrors.altContact}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="sp-section mt-3">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">📍 Address</h6>
                      <div className="row g-2">
                        <div className="col-12 col-md-6 col-lg-6">
                          <input 
                            className={`input-pill form-control ${validationErrors.address ? 'is-invalid' : ''}`}
                            placeholder="Address *" 
                            name="address" 
                            value={form.address} 
                            onChange={onChange} 
                          />
                          {validationErrors.address && (
                            <div className="invalid-feedback d-block">{validationErrors.address}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-6">
                          <input 
                            className={`input-pill form-control ${validationErrors.landmark ? 'is-invalid' : ''}`}
                            placeholder="Landmark (Near...) *" 
                            name="landmark" 
                            value={form.landmark} 
                            onChange={onChange} 
                          />
                          {validationErrors.landmark && (
                            <div className="invalid-feedback d-block">{validationErrors.landmark}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <Select
                            className={`input-pill ${validationErrors.country ? 'is-invalid' : ''}`}
                            placeholder="Country/Region *"
                            value={findOption(countryOptions, form.country)}
                            onChange={(selected) => {
                              handleLocationChange('country', selected);
                              if (validationErrors.country) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.country;
                                  return newErrors;
                                });
                              }
                            }}
                            options={countryOptions}
                            isClearable
                          />
                          {validationErrors.country && (
                            <div className="invalid-feedback d-block">{validationErrors.country}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <Select
                            className={`input-pill ${validationErrors.state ? 'is-invalid' : ''}`}
                            placeholder="State *"
                            value={findOption(stateOptions, form.state)}
                            onChange={(selected) => {
                              handleLocationChange('state', selected);
                              if (validationErrors.state) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.state;
                                  return newErrors;
                                });
                              }
                            }}
                            options={stateOptions}
                            isClearable
                            isDisabled={!form.country}
                          />
                          {validationErrors.state && (
                            <div className="invalid-feedback d-block">{validationErrors.state}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <Select
                            className={`input-pill ${validationErrors.city ? 'is-invalid' : ''}`}
                            placeholder="City *"
                            value={findOption(cityOptions, form.city)}
                            onChange={(selected) => {
                              handleLocationChange('city', selected);
                              if (validationErrors.city) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.city;
                                  return newErrors;
                                });
                              }
                            }}
                            options={cityOptions}
                            isClearable
                            isDisabled={!form.state}
                          />
                          {validationErrors.city && (
                            <div className="invalid-feedback d-block">{validationErrors.city}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <input 
                            className={`input-pill form-control ${validationErrors.pincode ? 'is-invalid' : ''}`}
                            placeholder="Pincode (000000) *" 
                            name="pincode" 
                            value={form.pincode} 
                            onChange={onChange} 
                          />
                          {validationErrors.pincode && (
                            <div className="invalid-feedback d-block">{validationErrors.pincode}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="sp-section mt-3">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">📊 Trading Information</h6>
                      <div className="form-grid d-grid gap-2">
                        <input className="input-pill form-control" placeholder="Total Revenue (Annual)" name="totalRevenue" value={form.totalRevenue} onChange={onChange} />
                        <input className="input-pill form-control" placeholder="Export Percentage (%)" name="exportPercentage" value={form.exportPercentage} onChange={onChange} />
                        <input className="input-pill form-control" placeholder="Company Overseas Address" name="overseasAddress" value={form.overseasAddress} onChange={onChange} />
                        <input className="input-pill form-control" placeholder="IEC Number" name="iecNumber" value={form.iecNumber} onChange={onChange} />
                      </div>
                    </div>
                  </section>

                  <section className="sp-section mt-3">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">🚚 Supply Chain Logistics</h6>
                      <div className="row g-2">
                        <div className="col-12 col-md-6 col-lg-3">
                          <input className="input-pill form-control" placeholder="Enter port name" name="nearestPort" value={form.nearestPort || ''} onChange={onChange} />
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="input-group input-group-sm">
                            <input className="form-control" placeholder="Enter Distance to the Factory" name="factoryToPortDistance" value={form.factoryToPortDistance || ''} onChange={onChange} />
                            <select className="form-select" name="factoryToPortDistanceUnit" value={form.factoryToPortDistanceUnit || 'km'} onChange={onChange} style={{ maxWidth: 120 }}>
                              {distanceUnitOptions.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <input className="input-pill form-control" placeholder="Enter airport name" name="nearestAirport" value={form.nearestAirport || ''} onChange={onChange} />
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="input-group input-group-sm">
                            <input className="form-control" placeholder="Enter Distance to Port/Airport" name="factoryToAirportDistance" value={form.factoryToAirportDistance || ''} onChange={onChange} />
                            <select className="form-select" name="factoryToAirportDistanceUnit" value={form.factoryToAirportDistanceUnit || 'km'} onChange={onChange} style={{ maxWidth: 120 }}>
                              {distanceUnitOptions.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                            </select>
                          </div>
                        </div>

                        <div className="col-12 col-md-6 col-lg-3">
                          <input className="input-pill form-control" placeholder="Enter highway name" name="nearestHighway" value={form.nearestHighway || ''} onChange={onChange} />
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="input-group input-group-sm">
                            <input className="form-control" placeholder="Enter Distance to the Factory" name="factoryToHighwayDistance" value={form.factoryToHighwayDistance || ''} onChange={onChange} />
                            <select className="form-select" name="factoryToHighwayDistanceUnit" value={form.factoryToHighwayDistanceUnit || 'km'} onChange={onChange} style={{ maxWidth: 120 }}>
                              {distanceUnitOptions.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="sp-section mt-3">
                    <div className="sp-subcard certificates-card">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="sp-section-title">📜 Certificates</h6>
                        <button 
                          type="button" 
                          className="btn btn-outline-secondary"
                          onClick={handleAddCertificate}
                        >
                          + Add More
                        </button>
                      </div>

                      {certificates.map((certificate, index) => (
                        <div key={index} className="sp-subcard mb-3" style={{ position: 'relative', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '15px' }}>
                          {certificates.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, width: '30px', height: '30px', padding: 0, borderRadius: '50%', fontSize: '18px', lineHeight: '1' }}
                              onClick={() => handleRemoveCertificate(index)}
                              title="Remove this certificate"
                            >
                              ×
                            </button>
                          )}
                          <div className="certificate-entry">
                            <div className="row g-2">
                              <div className="col-12 col-md-6 col-lg-3">
                                <input 
                                  className="input-pill form-control" 
                                  placeholder="Title *" 
                                  value={certificate.name || ''} 
                                  onChange={(e) => handleCertificateChange(index, 'name', e.target.value)} 
                                />
                              </div>
                              <div className="col-12 col-md-6 col-lg-3">
                                <input 
                                  className="input-pill form-control" 
                                  placeholder="Certification Company *" 
                                  value={certificate.issuer || ''} 
                                  onChange={(e) => handleCertificateChange(index, 'issuer', e.target.value)} 
                                />
                              </div>
                              <div className="col-12 col-md-6 col-lg-3">
                                <label className="form-label form-label-small">Date Issued</label>
                                <input 
                                  className="input-pill form-control" 
                                  type="date"
                                  value={certificate.issued_date || ''} 
                                  onChange={(e) => handleCertificateChange(index, 'issued_date', e.target.value)} 
                                />
                              </div>
                              <div className="col-12 col-md-6 col-lg-3">
                                <label className="form-label form-label-small">Expiry Date</label>
                                <input 
                                  className="input-pill form-control" 
                                  type="date"
                                  value={certificate.validity || ''} 
                                  onChange={(e) => handleCertificateChange(index, 'validity', e.target.value)} 
                                />
                              </div>
                            </div>

                            <div className="row g-2 mt-2">
                              <div className="col-12 col-md-6 col-lg-3">
                                <div className="file-upload-component">
                                  <div className="file-title">Attachment (jpg, jpeg and png formats are supported)</div>
                                  <label className="file-box d-block p-2 border rounded" style={{ cursor: 'pointer' }}>
                                    <div className="file-content d-flex align-items-center gap-2">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon">
                                        <path d="M12 3v12"></path><path d="m17 8-5-5-5 5"></path><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                      </svg>
                                      <span className="choose-text">
                                        {certificate.attachment ? certificate.attachment.name : certificate.attachment_url ? `✓ ${certificate.attachment_url.split('/').pop()}` : 'Choose file'}
                                      </span>
                                    </div>
                                    <input 
                                      type="file" 
                                      accept=".jpg,.jpeg,.png" 
                                      style={{ display: 'none' }} 
                                      onChange={(e) => handleCertificateFileChange(index, e)} 
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="sp-section mt-3">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">🕐 Timezone & Currency</h6>
                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <Select
                            className={`input-pill ${validationErrors.timezone ? 'is-invalid' : ''}`}
                            placeholder="Select Timezone *"
                            value={findOption(timezoneOptions, form.timezone)}
                            onChange={(opt) => {
                              onChange({ target: { name: "timezone", value: opt?.value || "" } });
                              if (validationErrors.timezone) {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.timezone;
                                  return newErrors;
                                });
                              }
                            }}
                            options={timezoneOptions}
                            isClearable
                          />
                          {validationErrors.timezone && (
                            <div className="invalid-feedback d-block">{validationErrors.timezone}</div>
                          )}
                        </div>
                        <div className="col-12 col-md-6">
                          <Select
                            className="input-pill"
                            placeholder="Select Currency"
                            value={findOption(currencyOptions, form.currency)}
                            onChange={(opt) => onChange({ target: { name: "currency", value: opt?.value || "" } })}
                            options={currencyOptions}
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <div className="sp-actions-row mb-4 mt-4 d-flex justify-content-end">
                    <div className="sp-left-actions" />
                    <div className="sp-right-actions">
                      <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              </TabPane>
            </Tabs>
          </main>
        </div>
      </div>
    </>
  );
}
